import React, { useEffect, useState } from 'react';
import { useTankCleaningStore } from '../../../context/TankCleaningContext';
import { PortCall } from '../../../types/ViewModel/PortCall';
import { Chemical } from '../../../types/ViewModel/TankCleaning/Chemical';
import { ChemicalRobReportItem } from '../../../types/ViewModel/TankCleaning/ROB/ChemicalRobReportItem';
import OdfjellInputComponent, { InputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { ChemicalCategoryList, ChemicalCategoryWrapper, RobItemRow } from './ReportRob.styles';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';

type Props = {
    chemicalRobReportItems: ChemicalRobReportItem[];
    previousPortCallWithRob?: PortCall;
    selectedPortCallId: number;
};

const ChemicalCategoryReportItemsComponent: React.FC<Props> = (props) => {
    const { chemicalRobUpdateReportData, setChemicalRobUpdateReportData, robLocationDDList } = useTankCleaningStore();
    const [ddValuesList, setDDValuesList] = useState<string[]>([]);
    const { checkIsTankCleaningReadOnly } = useReadOnlyUtils();

    // =============  Use Effects ================
    useEffect(() => {
        if (robLocationDDList && robLocationDDList.length > 0) {
            const _ddValuesList: string[] = [];
            robLocationDDList.forEach((robLocationDDCategory) => {
                robLocationDDCategory.robLocationDropdownItemList.forEach((robLocationDDItem) => {
                    _ddValuesList[robLocationDDItem.robLocationDropdownItemId] = robLocationDDItem.name;
                });
            });
            setDDValuesList(_ddValuesList);
        }
    }, [robLocationDDList]);

    const getDDList = (robLocationDropdownId: number) => {
        if (robLocationDropdownId) {
            const _cleaningChemicalsDD = robLocationDDList.find((robLocationDD) => robLocationDD.robLocationDropdownId === robLocationDropdownId);

            return _cleaningChemicalsDD?.robLocationDropdownItemList?.map((robLocationDropdownItem) => robLocationDropdownItem.name);
        }
    };

    const getDDListValue = (robLocationDropdownItemId: number) => {
        if (robLocationDropdownItemId && ddValuesList && ddValuesList.length > 0) {
            return ddValuesList[robLocationDropdownItemId];
        }
        return 0;
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;
        const type = changeObject.event.target.type;
        const selectedChemicalRobReportItem: ChemicalRobReportItem = changeObject.dataObject;

        updateReports(selectedChemicalRobReportItem, changeObject.extraArgs, name, value, type);
    };

    const updateReports = (selectedChemicalRobReportItem: ChemicalRobReportItem, index: number, name: string, value: string, type: string) => {
        const selectedChemical: Chemical = selectedChemicalRobReportItem.chemical;
        if (!selectedChemicalRobReportItem.chemicalRobItemLocationList) {
            selectedChemicalRobReportItem.chemicalRobItemLocationList = [];
        }
        if (!selectedChemicalRobReportItem.chemicalRobItemLocationList) {
            selectedChemicalRobReportItem.chemicalRobItemLocationList = [];
        }
        if (!selectedChemicalRobReportItem.chemicalRobItemLocationList[index]) {
            selectedChemicalRobReportItem.chemicalRobItemLocationList[index] = initChemicalRobLocationReportItem(selectedChemical.chemicalId);
        }
        if (type === 'text') {
            selectedChemicalRobReportItem.chemicalRobItemLocationList[index][name] = Number(value);
        } else if (type === 'select-one') {
            selectedChemicalRobReportItem.chemicalRobItemLocationList[index][name] = ddValuesList.findIndex((v) => v === value);
        }
        let aggrRobValue = 0;
        if (selectedChemicalRobReportItem.chemicalRobItemLocationList[0] && selectedChemicalRobReportItem.chemicalRobItemLocationList[0].robValue) {
            aggrRobValue += selectedChemicalRobReportItem.chemicalRobItemLocationList[0].robValue;
        }
        if (selectedChemicalRobReportItem.chemicalRobItemLocationList[1] && selectedChemicalRobReportItem.chemicalRobItemLocationList[1].robValue) {
            aggrRobValue += selectedChemicalRobReportItem.chemicalRobItemLocationList[1].robValue;
        }
        if (selectedChemicalRobReportItem.chemicalRobItemLocationList[2] && selectedChemicalRobReportItem.chemicalRobItemLocationList[2].robValue) {
            aggrRobValue += selectedChemicalRobReportItem.chemicalRobItemLocationList[2].robValue;
        }
        if (chemicalRobUpdateReportData?.chemicalRobItemList && chemicalRobUpdateReportData?.chemicalRobItemList.length > 0) {
            const chemicalRobItem = chemicalRobUpdateReportData?.chemicalRobItemList.find((o) => o.chemicalId === selectedChemical.chemicalId);
            if (chemicalRobItem) {
                if (!chemicalRobItem.chemicalRobItemLocationList) {
                    chemicalRobItem.chemicalRobItemLocationList = [];
                }
                if (!chemicalRobItem.chemicalRobItemLocationList[index]) {
                    chemicalRobItem.chemicalRobItemLocationList[index] = initRobLocationItem();
                }
                if (type === 'text') {
                    chemicalRobItem.chemicalRobItemLocationList[index].robValue = Number(value);
                } else if (type === 'select-one') {
                    chemicalRobItem.chemicalRobItemLocationList[index].robLocationDropdownItemId = ddValuesList.findIndex((v) => v === value);
                }

                chemicalRobItem.chemicalRobItemLocationList[index].locationIndex = index;

                chemicalRobItem.robValue = aggrRobValue;
            }
        }
        selectedChemicalRobReportItem.robValue = aggrRobValue;

        setChemicalRobUpdateReportData(chemicalRobUpdateReportData ? { ...chemicalRobUpdateReportData } : null);
    };

    const initChemicalRobLocationReportItem = (chemicalId: number) => {
        return {
            chemicalId: chemicalId,
            robLocationDropdownItemId: 0,
            robValue: 0,
        };
    };

    const initRobLocationItem = () => {
        return {
            chemicalRobItemLocationId: 0,
            robLocationDropdownItemId: 0,
            robValue: 0,
            locationIndex: 0,
        };
    };

    const getPreviousItemValue = (chemicalRobReportItem: ChemicalRobReportItem) => {
        const chemicalRobForPortCall = props.previousPortCallWithRob?.chemicalRobForPortCall;
        if (chemicalRobForPortCall) {
            const previousRobItem = chemicalRobForPortCall.chemicalRobItemList.find(
                (c) => c.chemicalId === chemicalRobReportItem.chemical.chemicalId
            );
            if (previousRobItem) {
                return previousRobItem.robValue;
            }
        }
        return 0;
    };

    const isReadOnly = checkIsTankCleaningReadOnly();

    return (
        <ChemicalCategoryWrapper>
            {ddValuesList && ddValuesList.length > 0 && props.chemicalRobReportItems && props.chemicalRobReportItems?.length > 0 && (
                <ChemicalCategoryList>
                    {props.chemicalRobReportItems.map((chemicalRobReportItem, index) => {
                        return (
                            <li key={`robReportItem_${props.selectedPortCallId}_${chemicalRobReportItem.chemical.chemicalId}_${index}`}>
                                <RobItemRow>
                                    <label title={chemicalRobReportItem.chemical.name}>{chemicalRobReportItem.chemical.name}</label>
                                    <span>
                                        <OdfjellInputComponent
                                            type={'number'}
                                            isReadOnly={true}
                                            value={getPreviousItemValue(chemicalRobReportItem)}
                                            name={'prevRobValue'}
                                            unit={chemicalRobReportItem.chemical.unit?.unitName ?? ''}
                                        />
                                    </span>
                                    <span className="location">
                                        <OdfjellInputComponent
                                            dataObject={chemicalRobReportItem}
                                            extraArgs={0}
                                            type={'select'}
                                            isReadOnly={isReadOnly}
                                            options={getDDList(chemicalRobReportItem.chemical.robLocationDropdownId)}
                                            value={getDDListValue(chemicalRobReportItem.chemicalRobItemLocationList[0]?.robLocationDropdownItemId)}
                                            name={'robLocationDropdownItemId'}
                                            onChange={onInputChange}
                                            selectDefaultOption={'Select location'}
                                        />
                                        <OdfjellInputComponent
                                            dataObject={chemicalRobReportItem}
                                            extraArgs={0}
                                            type={'number'}
                                            isReadOnly={isReadOnly}
                                            value={chemicalRobReportItem.chemicalRobItemLocationList[0]?.robValue}
                                            name={'robValue'}
                                            onChange={onInputChange}
                                            unit={chemicalRobReportItem.chemical.unit?.unitName ? chemicalRobReportItem.chemical.unit?.unitName : ''}
                                        />
                                    </span>
                                    <span className="location">
                                        <OdfjellInputComponent
                                            dataObject={chemicalRobReportItem}
                                            extraArgs={1}
                                            type={'select'}
                                            isReadOnly={isReadOnly}
                                            options={getDDList(chemicalRobReportItem.chemical.robLocationDropdownId)}
                                            value={getDDListValue(chemicalRobReportItem.chemicalRobItemLocationList[1]?.robLocationDropdownItemId)}
                                            name={'robLocationDropdownItemId'}
                                            onChange={onInputChange}
                                            selectDefaultOption={'Select location'}
                                        />
                                        <OdfjellInputComponent
                                            dataObject={chemicalRobReportItem}
                                            extraArgs={1}
                                            type={'number'}
                                            isReadOnly={isReadOnly}
                                            value={chemicalRobReportItem.chemicalRobItemLocationList[1]?.robValue}
                                            name={'robValue'}
                                            onChange={onInputChange}
                                            unit={chemicalRobReportItem.chemical.unit?.unitName ?? ''}
                                        />
                                    </span>
                                    <span className="location">
                                        <OdfjellInputComponent
                                            dataObject={chemicalRobReportItem}
                                            extraArgs={2}
                                            type={'select'}
                                            isReadOnly={isReadOnly}
                                            options={getDDList(chemicalRobReportItem.chemical.robLocationDropdownId)}
                                            value={getDDListValue(chemicalRobReportItem.chemicalRobItemLocationList[2]?.robLocationDropdownItemId)}
                                            name={'robLocationDropdownItemId'}
                                            onChange={onInputChange}
                                            selectDefaultOption={'Select location'}
                                        />
                                        <OdfjellInputComponent
                                            dataObject={chemicalRobReportItem}
                                            extraArgs={2}
                                            type={'number'}
                                            isReadOnly={isReadOnly}
                                            value={chemicalRobReportItem.chemicalRobItemLocationList[2]?.robValue}
                                            name={'robValue'}
                                            onChange={onInputChange}
                                            unit={chemicalRobReportItem.chemical.unit?.unitName ? chemicalRobReportItem.chemical.unit?.unitName : ''}
                                        />
                                    </span>

                                    <span className="aggr-rob">
                                        <OdfjellInputComponent
                                            type={'number'}
                                            isReadOnly={true}
                                            value={chemicalRobReportItem.robValue}
                                            name={'aggrRobValue'}
                                            unit={chemicalRobReportItem.chemical.unit?.unitName ?? ''}
                                        />
                                    </span>
                                </RobItemRow>
                            </li>
                        );
                    })}
                </ChemicalCategoryList>
            )}
        </ChemicalCategoryWrapper>
    );
};

export default ChemicalCategoryReportItemsComponent;
